/*
 * Copyright (c) 2026. Orion Analytics.
 * All Rights Reserved.
 */

import {
    TrendingUp,
    TrendingDown,
    Activity
} from "lucide-react";

function generateMarketCommentary(
    futuresData = [],
    optionsData = []
) {

    const commentary = [];

    const bullishFutures =
        futuresData.filter(
            d => d.signal === "Long Build-up"
        ).length;

    const bearishFutures =
        futuresData.filter(
            d => d.signal === "Short Build-up"
        ).length;

    const bullishOptions =
        optionsData.filter(
            d =>
                d.signal === "Put Writing" ||
                d.signal === "Long Build-up"
        ).length;

    const bearishOptions =
        optionsData.filter(
            d =>
                d.signal === "Call Writing" ||
                d.signal === "Short Build-up"
        ).length;

    if (bullishFutures > bearishFutures) {

        commentary.push(
            "Futures positioning remains bullish with Long Build-up signals dominating recent activity."
        );

    } else if (bearishFutures > bullishFutures) {

        commentary.push(
            "Futures traders are showing defensive behaviour with Short Build-up signals leading the market."
        );

    }

    if (bullishOptions > bearishOptions) {

        commentary.push(
            "Options flow suggests support creation through Put Writing and bullish positioning."
        );

    } else if (bearishOptions > bullishOptions) {

        commentary.push(
            "Options traders are actively writing Calls, indicating overhead resistance and cautious sentiment."
        );

    }

    if (commentary.length === 0) {

        commentary.push(
            "Positioning remains balanced with no strong directional bias visible across derivatives markets."
        );

    }

    return commentary;
}

export default function CommentaryCard({

    futuresData = [],
    optionsData = []

}) {

    const commentary =
        generateMarketCommentary(
            futuresData,
            optionsData
        );

    const bullishCount =
        futuresData.filter(
            d =>
                d.signal === "Long Build-up" ||
                d.signal === "Short Covering"
        ).length;

    const bearishCount =
        futuresData.filter(
            d =>
                d.signal === "Short Build-up" ||
                d.signal === "Long Unwinding"
        ).length;

    const sentiment =
        bullishCount > bearishCount
            ? "Bullish"
            : bearishCount > bullishCount
            ? "Bearish"
            : "Neutral";

    return (

        <div
            className="
                bg-slate-950
                border
                border-slate-800
                rounded-3xl
                p-6
            "
        >

            <div
                className="
                    flex
                    items-center
                    justify-between
                    mb-6
                "
            >

                <div>

                    <h2
                        className="
                            text-xl
                            font-bold
                            text-white
                        "
                    >
                        Derivatives Commentary
                    </h2>

                    <p
                        className="
                            text-sm
                            text-slate-400
                        "
                    >
                        Automated interpretation of
                        futures & options positioning
                    </p>

                </div>

                <div
                    className="
                        flex
                        items-center
                        gap-2
                    "
                >

                    {sentiment === "Bullish" ? (

                        <TrendingUp
                            size={22}
                            className="
                                text-emerald-400
                            "
                        />

                    ) : sentiment === "Bearish" ? (

                        <TrendingDown
                            size={22}
                            className="
                                text-red-400
                            "
                        />

                    ) : (

                        <Activity
                            size={22}
                            className="
                                text-yellow-400
                            "
                        />

                    )}

                    <span
                        className={`
                            font-semibold
                            ${
                                sentiment === "Bullish"
                                    ? "text-emerald-400"
                                    : sentiment === "Bearish"
                                    ? "text-red-400"
                                    : "text-yellow-400"
                            }
                        `}
                    >
                        {sentiment}
                    </span>

                </div>

            </div>

            <div className="space-y-4">

                {commentary.map(
                    (text, idx) => (

                        <div
                            key={idx}
                            className="
                                bg-slate-900
                                border
                                border-slate-800
                                rounded-xl
                                p-4
                                text-slate-300
                                leading-relaxed
                            "
                        >
                            {text}
                        </div>

                    )
                )}

            </div>

        </div>

    );

}